import React, { HTMLProps } from 'react';
import { useSmartProp, useEditor } from '@smartfire/js-block-engine';

import * as BlockEditor from '@wordpress/block-editor';
import type { RichText as RT } from '@wordpress/block-editor';

const RichText = BlockEditor?.RichText as RichTextOverride;

declare type RichTextOverride = {
  <T extends keyof HTMLElementTagNameMap = 'div'>(
    props: React.HTMLProps<T> &
      RT.Props<T> & {
        allowedFormats: any;
      },
  ): JSX.Element;
  /**
   * Should be used in the `save` function of your block to correctly save rich text content.
   */
  Content<T extends keyof HTMLElementTagNameMap = 'div'>(props: RT.ContentProps<T>): JSX.Element;
  isEmpty(value: string | string[]): boolean;
};

export interface EditableTextProps<T extends keyof HTMLElementTagNameMap>
  extends Partial<React.HTMLProps<T>> {
  smartProp: string;
  tagName?: T;
  tagProps?: HTMLProps<T>;
  render?: (value: string, props: HTMLProps<T>, TagName: T) => React.ReactElement;
}

export default function EditableText<T extends keyof HTMLElementTagNameMap = 'div'>({
  smartProp,
  className = undefined,
  style = undefined,
  tagName = 'div' as T,
  tagProps = {},
  render = (value, renderProps, TagName) =>
    React.createElement(TagName, {
      dangerouslySetInnerHTML: { __html: value },
      ...(renderProps ?? {}),
    }),
  ...props
}: EditableTextProps<T>): React.ReactElement {
  const [value, updateValue] = useSmartProp<string>(smartProp);
  const { isEditing } = useEditor();
  if (isEditing) {
    return (
      <RichText
        tagName={tagName}
        placeholder="..."
        allowedFormats={[]}
        // @ts-ignore // todo(improvement): remove ts ignore
        value={value}
        className={`${className} editableInput`}
        // @ts-ignore // todo(improvement): remove ts ignore
        onChange={updateValue}
        {...props}
      />
    );
  }

  return render(
    value,
    {
      className,
      style,
      ...tagProps,
    },
    tagName,
  );
}
